import { useState } from "react";
import { Check, ChevronsUpDown, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AssigneeAvatar } from "@/domains/pm/components/shared/AssigneeAvatar";
import type { UserProfile } from "@/domains/pm/types";

function displayName(user: UserProfile): string {
  const name = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
  return name || user.email;
}

interface UserMultiSelectListProps {
  users: UserProfile[];
  selectedUserIds: number[];
  onChange: (selectedIds: number[]) => void;
  searchPlaceholder?: string;
  emptyMessage?: string;
  /**
   * Users to list first under `suggestedLabel` (e.g. people responsible for
   * the issue's disciplines); the rest follow under `othersLabel`. Ids not in
   * `users` are ignored, so a suggestion can never add someone the list would
   * not offer.
   */
  suggestedUserIds?: readonly number[];
  suggestedLabel?: string;
  othersLabel?: string;
}

/**
 * The searchable avatar checklist used by `UserMultiSelect`. Extracted so
 * the same picker UI can be embedded in other surfaces — e.g. the PM issue
 * right-click context menu (WHA-3428).
 */
export function UserMultiSelectList({
  users,
  selectedUserIds,
  onChange,
  searchPlaceholder = "Search users…",
  emptyMessage = "No users found.",
  suggestedUserIds,
  suggestedLabel,
  othersLabel,
}: UserMultiSelectListProps) {
  const byId = new Map(users.map((u) => [u.id, u]));
  const suggested = (suggestedUserIds ?? [])
    .map((id) => byId.get(id))
    .filter((u): u is UserProfile => u !== undefined);
  const suggestedIds = new Set(suggested.map((u) => u.id));
  const others = users.filter((u) => !suggestedIds.has(u.id));
  const toggle = (id: number) => {
    onChange(
      selectedUserIds.includes(id)
        ? selectedUserIds.filter((x) => x !== id)
        : [...selectedUserIds, id]
    );
  };

  const renderItem = (user: UserProfile) => {
    const isSelected = selectedUserIds.includes(user.id);
    const name = displayName(user);
    // displayName falls back to email when first/last name is
    // empty — avoid rendering the email as a subtext in that
    // case (it'd duplicate the name).
    const hasName = name !== user.email;
    // cmdk uses `value` as the item's identity for filtering
    // and hover/keyboard navigation. Two users with the same
    // display name previously shared the same value, so cmdk
    // merged them and hovering one highlighted both
    // (WHA-3316). Append the email — guaranteed unique per
    // user — so each item is distinct, and so typing part of
    // an email matches via the same fuzzy filter.
    return (
      <CommandItem
        key={user.id}
        value={hasName ? `${name} ${user.email}` : user.email}
        onSelect={() => toggle(user.id)}
      >
        <div
          className={cn(
            "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
            isSelected ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible"
          )}
        >
          <Check className="h-3 w-3" />
        </div>
        <AssigneeAvatar user={user} size={20} className="mr-2" />
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-xs">{name}</span>
          {hasName && (
            <span className="truncate text-[10px] text-muted-foreground">{user.email}</span>
          )}
        </div>
      </CommandItem>
    );
  };

  return (
    <Command>
      <CommandInput placeholder={searchPlaceholder} />
      <CommandList>
        <CommandEmpty>{emptyMessage}</CommandEmpty>
        {suggested.length > 0 && (
          <CommandGroup heading={suggestedLabel}>{suggested.map(renderItem)}</CommandGroup>
        )}
        <CommandGroup heading={suggested.length > 0 ? othersLabel : undefined}>
          {others.map(renderItem)}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

interface UserMultiSelectProps {
  users: UserProfile[];
  selectedUserIds: number[];
  onChange: (selectedIds: number[]) => void;
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  /** Trigger sizing. `lg` enlarges the inline avatars + chevron ~35% for the
   *  expanded issue panel's meta bar. The dropdown list is unaffected. */
  size?: "sm" | "lg";
  /** Show the selected users' names next to their avatars (WHA-3810) — the
   *  issue detail panel's Assignee field needs labelled, readable values
   *  rather than a bare avatar stack. Every selected user is rendered; the
   *  trigger wraps onto further lines when they don't fit on one. */
  showNames?: boolean;
  /** See `UserMultiSelectList`. */
  suggestedUserIds?: readonly number[];
  suggestedLabel?: string;
  othersLabel?: string;
}

export function UserMultiSelect({
  users,
  selectedUserIds,
  onChange,
  disabled = false,
  placeholder = "Select users…",
  searchPlaceholder = "Search users…",
  emptyMessage = "No users found.",
  className,
  size = "sm",
  showNames = false,
  suggestedUserIds,
  suggestedLabel,
  othersLabel,
}: UserMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const selected = users.filter((u) => selectedUserIds.includes(u.id));
  const avatarSize = size === "lg" ? 27 : 20;
  const iconClass = size === "lg" ? "h-5 w-5" : "h-4 w-4";

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
        >
          {selected.length === 0 ? (
            <div className="flex items-center gap-2">
              <Users className={cn("shrink-0 opacity-50", iconClass)} />
              <span className="truncate text-muted-foreground">{placeholder}</span>
            </div>
          ) : showNames ? (
            // Every assignee is named, not just the first — the list rows show
            // all of them, so the detail panel must not silently collapse the
            // rest into a "+N". Wraps onto further lines when the column is
            // narrow (the trigger grows with it).
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
              {selected.map((u) => (
                <span key={u.id} className="flex min-w-0 items-center gap-2">
                  <AssigneeAvatar user={u} size={avatarSize} />
                  <span className="truncate">{displayName(u)}</span>
                </span>
              ))}
            </div>
          ) : (
            <div className="flex min-w-0 items-center -space-x-1.5">
              {selected.slice(0, 5).map((u) => (
                <AssigneeAvatar
                  key={u.id}
                  user={u}
                  size={avatarSize}
                  className="ring-1 ring-background"
                />
              ))}
              {selected.length > 5 && (
                <span className="ml-1.5 inline-flex h-5 items-center px-1 text-[10px] font-medium text-muted-foreground">
                  +{selected.length - 5}
                </span>
              )}
            </div>
          )}
          <ChevronsUpDown className={cn("ml-2 shrink-0 opacity-50", iconClass)} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <UserMultiSelectList
          users={users}
          selectedUserIds={selectedUserIds}
          onChange={onChange}
          searchPlaceholder={searchPlaceholder}
          emptyMessage={emptyMessage}
          suggestedUserIds={suggestedUserIds}
          suggestedLabel={suggestedLabel}
          othersLabel={othersLabel}
        />
      </PopoverContent>
    </Popover>
  );
}
